interface IconProps {
  className?: string
  size?: number
  'aria-hidden'?: boolean
}

type ChevronDirection = 'up' | 'down' | 'left' | 'right'

interface ChevronProps extends IconProps {
  direction?: ChevronDirection
}

const chevronRotation: Record<ChevronDirection, string> = {
  down: 'rotate-0',
  up: 'rotate-180',
  left: 'rotate-90',
  right: '-rotate-90',
}

/**
 * Downward chevron by default; `direction` rotates it.
 * Path copied from SearchBox.tsx / job-detail.tsx inline chevrons.
 */
export function Chevron({
  className = '',
  size = 12,
  direction = 'down',
  'aria-hidden': ariaHidden = true,
}: ChevronProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={ariaHidden}
      className={`${chevronRotation[direction]} ${className}`}
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  )
}

/**
 * Close / X icon. Path copied from job-detail.tsx inline close button.
 */
export function Close({ className = '', size = 20, 'aria-hidden': ariaHidden = true }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={ariaHidden}
      className={className}
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  )
}

/**
 * Search / magnifier icon. Shape copied from SearchBox.tsx inline search svg.
 */
export function Search({ className = '', size = 18, 'aria-hidden': ariaHidden = true }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden={ariaHidden}
      className={className}
    >
      <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
      <path d="M16 16L21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  )
}

/**
 * Checkmark icon. Polyline copied from job-detail.tsx inline check svg.
 */
export function Check({ className = '', size = 20, 'aria-hidden': ariaHidden = true }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={ariaHidden}
      className={className}
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  )
}

export type { IconProps, ChevronProps, ChevronDirection }
